// eslint-disable-next-line
import { action,runInAction, makeAutoObservable } from "mobx";
import { rootContainer, TYPE } from "../../../services/container/root";
import { ISaveable } from "../../../stores/types";
import LocalesService, { ILocaleItem } from "../../../services/api/locales-service";
import LocaleStore from "./locale-store";
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { IFileData } from "../../../components/file-upload/file-upload";

export class LocalesListStore implements ISaveable{

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }
    
    private _isLoading: boolean = false;
    private _localesList: LocaleStore[] = [];
    private _isSaving: boolean = false;
    private _isDeleting: boolean = false;
    private _isAdding: boolean = false;
    private _selectLocalesList: IOption[] = [] as IOption[];
    private _isDownloading: boolean = false;
    private _isUploading: boolean = false;
    private _uploadedSuccessfuly: boolean = false;
    private _isAddLanguageWizardOpen: boolean = false;

    
    protected get service(): LocalesService { return rootContainer.get<LocalesService>(TYPE.LocaleService); }
    public get isAddLanguageWizardOpen(): boolean { return this._isAddLanguageWizardOpen; }
    public get localesList(): LocaleStore[] { return this._localesList };
    public get selectLocalesList(): IOption[] { 
        const filteredList  = this._selectLocalesList.filter(item => {
            return !this.localesList.some(l => l.localeCode === item.value);
        });
        return filteredList.sort((a, b) => (a.label > b.label) ?1 :-1);
    };

    // Apparently, selectLocalesList takes word "select" from UI pattern, as it's used after all in <select>
    // makes more sense to me to not tightly couple method name to UI elements
    // it just contains all the available locales in an unfiltered list
    // During CR, acknowledged that we're aware of the naming issue in this store.
    // To be handled separately in https://returngo.atlassian.net/browse/ET-1051
    public get getAllLocales(): IOption[] {
        return this._selectLocalesList;
    }
    
    public isLoading(): boolean {return this._isLoading};
    public isSaving(): boolean {return this._isSaving};
    public isDeleting(): boolean { return this._isDeleting};
    public isDownloading(): boolean { return this._isDownloading};
    public isUploading(): boolean { return this._isUploading};
    public isBusy(): boolean { return this._isLoading || this._isSaving || this._isDeleting || this._isAdding };
    public hasChanges(): boolean { return this._localesList.some(locale => locale.hasChanges()) };
    public uploadedSuccessfuly(): boolean {return this._uploadedSuccessfuly }

    @action.bound
    async load() {
        this._isLoading = true;
        this.getSelectLocalesList();
		const localesData = await this.service.fetchShopLocales();
		runInAction(() => {
			this._setLocalesList(localesData);
            this._isLoading = false;
		});
	}

    public async save(): Promise<void> {
       this._isSaving = true;
       let localesToSave = [] as ILocaleItem[];
       this.localesList.forEach(locale => {
           localesToSave.push(locale.getUpdatedModel());
       });
       const __old = this.localesList.map(l => l.model);
       const savedLocalesList = await this.service.handleSave({ payload: localesToSave, __old });
        runInAction(() => {
            this._setLocalesList(savedLocalesList);
            this._isSaving = false;
       })
    }

    public async handleAddLocales(locales: string[]): Promise<boolean> {
        let isSucceeded = false;
        try {
            this._isAdding = true;
            const newLocalesList = await this.service.addLocales(locales);
            runInAction(() => {
                this._setLocalesList(newLocalesList);
                this._isAdding = false;
            });
            isSucceeded = true;
        }
        catch (e) {
            runInAction(() => {
                this._isAdding = false;
            });
        }
        return isSucceeded;
    }

    public async downloadTranslations(): Promise<void> {
        try{
            this._isDownloading = true;
            const path = await this.service.downloadTranslations();
            window.open(path);
            runInAction(() => {
                this._isDownloading = false;
            });
        } catch {
            runInAction(() => {
                this._isDownloading = false;
            });
        }

    }

    public async uploadTranslationsFile(file: File, fileContent: string | ArrayBuffer | null | undefined): Promise<boolean> {
        let isSuccesded = false;
        try {
            if (fileContent) {
                this._uploadedSuccessfuly = false;
                this._isUploading = true;
                const data: IFileData = {
                    file_data: fileContent,
                    mime_type: file.type,
                    original_name: file.name,
                    size: file.size,
                }
                const updatedLocales: ILocaleItem[] = await this.service.uploadTranslationsFile(data);

                if(!updatedLocales) {
                    this._isUploading = false;
                    throw new Error('Error: did not receive updated locales');
                }

                runInAction(() => {
                    /** Detect new locales */
                    updatedLocales.forEach((newLocale) => {
                        if (!this._localesList.some((locale) => locale.localeCode === newLocale.locale)) {
                            sessionStorage.setItem(newLocale.locale, "new");
                        }
                    })
                    /** Update locales list */
                    this._setLocalesList(updatedLocales);
                    this._isUploading = false;
                    this._uploadedSuccessfuly = true;
                })
                isSuccesded = true;
            } else {
                this._isUploading = false;
                throw new Error('no files to upload');
            }
        } catch (e) {
            runInAction(() => {
                this._isUploading = false;
            });
            isSuccesded = false;
        }
        return isSuccesded;
    }

    private async getSelectLocalesList(): Promise<void> {
        const selectedLocalesList = await this.service.getSelectLocalesList();
        runInAction(() => {
            this._selectLocalesList = selectedLocalesList;
        });
    }

    private onDefaultLocaleChange(locale: string): void {
        this._localesList.forEach(store => {
            if (store.localeCode !== locale) {
                store.unsetDefault();
            }
        });
    }

    private async onDelete(localeToDelete: string): Promise<void> {
        this._isDeleting = true;
        const __old = this.localesList.map(l => l.model);
        const updatedLocalesList = await this.service.handleDelete({ payload: { locale: localeToDelete }, __old })
        runInAction(() => {
            this._setLocalesList(updatedLocalesList);
            this._isDeleting = false;
        })
    }

    private getLocaleName(code: string): string {
        const localeName = this._selectLocalesList && this._selectLocalesList.find(locale => locale.value === code)?.label;
        return localeName ?? code;
    }

    private _setLocalesList(localesData: ILocaleItem[]): void {
        this._localesList = localesData.map(model => {
            return new LocaleStore(model, this.onDefaultLocaleChange, this.onDelete, this.getLocaleName);
        });
    }


    public setIsAddLanguageWizardOpen(v: boolean): void {
        this._isAddLanguageWizardOpen = v;
    }

}

export default new LocalesListStore();